import { z } from 'zod';
/**
 * Rhythm: which side of the holdout each user is on, where they are in their
 * life with the app, and the numbers the engine is judged against.
 *
 * "Rhythm" is an internal name. Users see "streak" and "weeks", and nothing
 * here is ever sent to the app or to analytics.
 */
export const RHYTHM_ARMS = ['rhythm', 'holdout'];
export const rhythmArmSchema = z.enum(RHYTHM_ARMS);
/** In the order the console lists them: present states, then the three absences. */
export const RHYTHM_STATES = [
    'new',
    'building',
    'steady',
    'returning',
    'slipping',
    'lapsed',
    'dormant',
];
export const rhythmStateSchema = z.enum(RHYTHM_STATES);
/** How a session of the app began. */
export const APP_SESSION_ENTRIES = ['icon', 'push', 'widget', 'shortcut', 'link', 'unknown'];
export const appSessionEntrySchema = z.enum(APP_SESSION_ENTRIES);
/** Whose baseline: everyone, or one arm. A user Rhythm has not placed is in neither arm. */
export const rhythmBaselineArmSchema = z.enum(['all', ...RHYTHM_ARMS]);
export const adminRhythmOverviewQuerySchema = z.object({
    arm: rhythmBaselineArmSchema.default('all'),
});
/** Complete Monday-based weeks the baseline table covers. */
export const RHYTHM_BASELINE_WEEKS = 4;
/**
 * A share as its two counts, never as a rate: a week with nobody in the
 * denominator has no rate, and the console says so instead of printing 0%.
 */
const shareSchema = z.object({
    count: z.number().int(),
    of: z.number().int(),
});
/** The optional coaching categories a user can switch off. */
const perCategorySchema = z.object({
    reminders: z.number().int(),
    summaries: z.number().int(),
    tips: z.number().int(),
});
/**
 * One complete week of the baseline. Every share is over a window that had
 * closed by the end of this week, so a row does not change once it appears.
 *
 * "Active" in this table means a logged meal, a completed workout or a
 * weigh-in that week. App opens have no history before sessions are recorded,
 * and a baseline has to mean the same thing in every row.
 */
export const adminRhythmBaselineWeekSchema = z.object({
    /** The week's Monday, as a local date. */
    weekStart: z.string(),
    activeUsers: z.number().int(),
    /**
     * Users with a recorded app session that week, by their own local dates.
     * Not part of "active" above, which stays a logged action in every row.
     * Null for a week that ended before the first session was recorded. Only
     * app builds with the session recorder send sessions, so this rises as
     * people update: read it against itself, not against the logged figure.
     */
    openedApp: z.number().int().nullable(),
    /** Of the week's active users, those whose kept days reached their weekly target. */
    keptWeek: shareSchema,
    /** Of users active in each of the two weeks before, those active in this one. */
    currentUserRetention: shareSchema,
    /** Of sign-ups whose first seven days ended this week, those with three or more kept days in them. */
    weekOneActivation: shareSchema,
    /** Of sign-ups whose day 1, 7 or 30 fell in this week, those active on that day. */
    d1: shareSchema,
    d7: shareSchema,
    d30: shareSchema,
    /** Of users who began slipping the week before, those back within seven days. */
    reactivation: shareSchema,
    /**
     * Of evening streak nudges sent that week, those followed by a qualifying
     * action (a meal, a completed workout or a weigh-in reaching the server)
     * later the same local day. Phase 2's exit compares the two arms on it.
     */
    actedAfterEveningNudge: shareSchema,
    /**
     * Of the users who kept a day that week (by the ledger), those whose chain
     * stood at seven days or more as the week ended. Phase 3's exit figure. Null
     * for a week the ledger has no rows for.
     */
    sevenDayStreak: shareSchema.nullable(),
    guardrails: z.object({
        /** Optional coaching pushes sent, from the fatigue ledger. */
        sends: perCategorySchema,
        /** Users who switched a category's push off. */
        optOuts: perCategorySchema,
        /** Devices whose notification permission was turned off in the OS. */
        osDenied: z.number().int(),
        /** Devices whose push token the provider rejected: the app was removed. */
        providerRejected: z.number().int(),
    }),
});
/**
 * What a user just did that makes a pitch worth making, in the order the
 * server tries them. The app has a card for each.
 */
export const RHYTHM_PITCH_HOOKS = ['workout_reminders', 'weekly_summary', 'streak'];
/** How long after a yes the pitch's exit figure asks whether the category is still on. */
export const RHYTHM_PITCH_KEPT_DAYS = 30;
export const adminRhythmOverviewSchema = z.object({
    generatedAt: z.string(),
    /** The `rhythm` feature switch. Off, no profile is written and the counts below stand still. */
    enabled: z.boolean(),
    /** `RHYTHM_HOLDOUT_PERCENT` in the running API: the share of new profiles held out. */
    holdoutPercent: z.number().int(),
    /** Accounts that are not deactivated. */
    users: z.number().int(),
    /** Of those, the ones Rhythm has given an arm and a state. */
    profiles: z.number().int(),
    arms: z.object({ rhythm: z.number().int(), holdout: z.number().int() }),
    /** Every state, in `RHYTHM_STATES` order, zeros included. */
    states: z.array(z.object({ state: rhythmStateSchema, count: z.number().int() })),
    /**
     * Users with an app open or a logged action in the last seven local days.
     * The figure the holdout is shrunk on, read from the source tables, so it
     * is right whether or not Rhythm is switched on.
     */
    weeklyActives: z.number().int(),
    /**
     * Phase 1's exit figure: of the installations seen in the last seven days
     * on a build that records sessions, those that sent one in that time. A
     * build records from the lowest build number that has sent a session on its
     * platform. Null until any build has sent one.
     */
    sessionRecording: shareSchema.nullable(),
    /** State changes by pair, most frequent first. `from` is null for a user's first placement. */
    transitions: z.array(z.object({
        from: rhythmStateSchema.nullable(),
        to: rhythmStateSchema,
        last7Days: z.number().int(),
        last28Days: z.number().int(),
    })),
    /**
     * Personal send times (phase 2), for the Rhythm arm: how many have their
     * own time in each slot, and the evening times themselves. Worked out daily
     * whether or not they are switched on.
     */
    timing: z.object({
        /** `RHYTHM_TIMING_ENABLED`: whether these times reach the schedule. */
        enabled: z.boolean(),
        /** Of Rhythm-arm profiles, those with their own time per slot. */
        personal: z.object({ evening: shareSchema, morning: shareSchema, sunday: shareSchema }),
        /** Rhythm-arm profiles' own evening times, by local minute, earliest first. */
        evening: z.array(z.object({ minute: z.number().int(), count: z.number().int() })),
    }),
    /**
     * The permission pitch (phase 5): offers to switch a notification category
     * on, by hook, over all time. Every hook is listed, zeros included.
     */
    pitches: z.object({
        /** `RHYTHM_PITCH_ENABLED`: whether `GET /rhythm` carries pitches. */
        enabled: z.boolean(),
        hooks: z.array(z.object({
            hook: z.enum(RHYTHM_PITCH_HOOKS),
            /** Handed to the app. */
            offered: z.number().int(),
            /** Of those, the ones the app drew. The exit rate is `accepted` over this. */
            shown: z.number().int(),
            accepted: z.number().int(),
            dismissed: z.number().int(),
            /**
             * Of the ones accepted `RHYTHM_PITCH_KEPT_DAYS` or more ago, those
             * whose category push is on today.
             */
            stillOn: shareSchema,
        })),
    }),
    /** Which users the baseline counts: everyone, or one arm by its members today. */
    baselineArm: rhythmBaselineArmSchema,
    /** The last `RHYTHM_BASELINE_WEEKS` complete weeks, oldest first. */
    baseline: z.array(adminRhythmBaselineWeekSchema),
});
/** How many of a user's state changes the panel shows. */
export const USER_RHYTHM_TRANSITIONS = 20;
/** The days of a user's ledger the panel shows, newest last. */
export const USER_RHYTHM_LEDGER_DAYS = 35;
export const RHYTHM_DAY_KINDS = ['kept', 'rest', 'passed', 'missed'];
export const rhythmDayKindSchema = z.enum(RHYTHM_DAY_KINDS);
/** How many of a user's scheduled pushes the panel lists. */
export const USER_RHYTHM_UPCOMING = 12;
/** Days of sessions the user's hour-of-week grid counts: the window personal timing reads. */
export const USER_RHYTHM_SESSION_DAYS = 56;
export const adminUserRhythmSchema = z.object({
    enabled: z.boolean(),
    /** The user's fixed place in the holdout split, 0 to 99. Below the holdout share is held out. */
    bucket: z.number().int(),
    /** Null until Rhythm has looked at this account. */
    profile: z
        .object({
        arm: rhythmArmSchema,
        state: rhythmStateSchema,
        stateSince: z.string(),
        /** The last local date with an app open or a logged action. */
        lastActiveDate: z.string().nullable(),
        /** The last active date before the absence they are in. Null while they are here. */
        lapseStartDate: z.string().nullable(),
        /** The last local date their day was closed for. */
        closedThrough: z.string().nullable(),
        createdAt: z.string(),
        /**
         * Their own send times in local minutes, per slot, or null where the
         * default applies. Used only in the Rhythm arm, and only with timing on.
         */
        timing: z.object({
            evening: z.number().int().nullable(),
            morning: z.number().int().nullable(),
            sunday: z.number().int().nullable(),
            any: z.number().int().nullable(),
            /** The local date they were last worked out. */
            computedOn: z.string().nullable(),
        }),
    })
        .nullable(),
    /** Newest first, at most `USER_RHYTHM_TRANSITIONS`. */
    transitions: z.array(z.object({
        from: rhythmStateSchema.nullable(),
        to: rhythmStateSchema,
        localDate: z.string(),
        occurredAt: z.string(),
    })),
    /**
     * When and how the app was opened, over the last `USER_RHYTHM_SESSION_DAYS`
     * days. Hours and weekdays are the user's local ones, as recorded.
     */
    /**
     * The user's scheduled pushes from now, soonest first, at most
     * `USER_RHYTHM_UPCOMING`: the event type, the local date and time it is set
     * for, and its state. Where Rhythm's timing applies, the time shows it.
     */
    upcoming: z.array(z.object({
        eventType: z.string(),
        localDate: z.string(),
        localTime: z.string(),
        status: z.string(),
    })),
    /**
     * The streak's ledger, the last `USER_RHYTHM_LEDGER_DAYS` closed days it
     * holds, oldest first. A date missing from the run is a day away: the
     * ledger stops after thirty days with nothing kept.
     */
    ledger: z.array(z.object({
        date: z.string(),
        kind: rhythmDayKindSchema,
        chainDays: z.number().int(),
        chainWeeks: z.number().int(),
    })),
    sessions: z.object({
        total: z.number().int(),
        /** Seven rows, Monday first, each 24 counts, one per local hour. */
        hourOfWeek: z.array(z.array(z.number().int())),
        /** Every entry point, in `APP_SESSION_ENTRIES` order, zeros included. */
        entries: z.array(z.object({ entry: appSessionEntrySchema, count: z.number().int() })),
        /** The newest session's start, at any age. Null when none is stored. */
        lastStartedAt: z.string().nullable(),
    }),
});
//# sourceMappingURL=rhythm.js.map